import React, { useEffect, useRef, useState } from "react";
import { gsap, useGSAP, EASE, prefersReducedMotion } from "../../lib/gsap";
import { usePreloader } from "../../context/PreloaderContext";
import { personalInfo } from "../../data/portfolio";

const CRITICAL = ["/me.webp", "/Projects/lumina-poster.jpg"];

/**
 * Load screen: the name is set on a dark ground with one rule filling beneath
 * it, then the screen splits and the two halves clear the frame. Same easing
 * and rhythm as the hero entrance, so the site opens as one movement instead
 * of a preloader followed by a separate animation.
 */
const Boot = () => {
  const { setIsPreloaderDone } = usePreloader();
  const scope = useRef(null);
  const [gone, setGone] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let settled = 0;

    const finish = () => {
      settled += 1;
      if (settled >= CRITICAL.length && !cancelled) setReady(true);
    };

    CRITICAL.forEach((src) => {
      const img = new Image();
      img.onload = finish;
      img.onerror = finish;
      img.src = src;
    });

    const bail = window.setTimeout(() => !cancelled && setReady(true), 2600);

    return () => {
      cancelled = true;
      window.clearTimeout(bail);
    };
  }, []);

  useGSAP(
    () => {
      if (!ready) return undefined;

      const done = () => {
        setIsPreloaderDone(true);
        setGone(true);
        window.lenis?.start();
      };

      window.lenis?.stop();

      if (prefersReducedMotion()) {
        gsap.to(scope.current, { autoAlpha: 0, duration: 0.25, onComplete: done });
        return undefined;
      }

      const tl = gsap.timeline({ onComplete: done });

      tl.to("[data-boot-fill]", { scaleX: 1, duration: 0.62, ease: EASE.io }, 0)
        .to("[data-boot-word]", { yPercent: -110, duration: 0.42, ease: EASE.io, stagger: 0.05 }, 0.5)
        .to("[data-boot-meta]", { opacity: 0, duration: 0.22 }, 0.5)
        .to("[data-boot-half='top']", { yPercent: -100, duration: 0.72, ease: EASE.io }, 0.66)
        .to("[data-boot-half='bottom']", { yPercent: 100, duration: 0.72, ease: EASE.io }, 0.66);

      return () => tl.kill();
    },
    { scope, dependencies: [ready] }
  );

  if (gone) return null;

  return (
    <div ref={scope} className="fixed inset-0 z-boot" aria-hidden="true">
      {/* Two halves that clear the frame in opposite directions */}
      <div
        data-boot-half="top"
        className="absolute inset-x-0 top-0 h-1/2 bg-ink"
        style={{ willChange: "transform" }}
      />
      <div
        data-boot-half="bottom"
        className="absolute inset-x-0 bottom-0 h-1/2 bg-ink"
        style={{ willChange: "transform" }}
      />

      <div className="absolute inset-0 flex flex-col justify-between px-margin py-6 text-bone">
        <span data-boot-meta className="t-label text-ash">
          {personalInfo.location}
        </span>

        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-wrap gap-x-[0.3em]">
            {personalInfo.fullName.split(" ").map((word) => (
              <span key={word} className="mask-line">
                <span
                  data-boot-word
                  className="block uppercase text-bone"
                  style={{
                    fontSize: "clamp(2.25rem, 8vw, 6rem)",
                    lineHeight: 0.92,
                    letterSpacing: "-0.04em",
                    fontVariationSettings: "'wdth' 88, 'wght' 500",
                  }}
                >
                  {word}
                </span>
              </span>
            ))}
          </div>

          <div className="h-px w-full max-w-[34rem] bg-bone/25">
            <div
              data-boot-fill
              className="h-px w-full origin-left bg-bone"
              style={{ transform: "scaleX(0)" }}
            />
          </div>
        </div>

        <span data-boot-meta className="t-label text-ash">
          {personalInfo.title}
        </span>
      </div>
    </div>
  );
};

export default Boot;
